import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Users, BookOpen, Calendar, BarChart, Bell, Image, Flag, HelpCircle, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';

function StatCard({ icon: Icon, label, value, accent = 'text-teal-400', onClick }) {
  return (
    <button onClick={onClick} disabled={!onClick}
      className={`bg-[#0d1f35] rounded-2xl p-5 border border-slate-800 text-left transition-colors ${onClick ? 'hover:border-teal-500/40 cursor-pointer' : 'cursor-default'}`}>
      <div className="flex items-center gap-2.5 mb-2">
        <Icon className={`w-5 h-5 ${accent}`} />
        <h3 className="font-medium text-slate-400 text-sm">{label}</h3>
      </div>
      <p className="text-3xl font-bold text-white font-['Azeret_Mono']">{value}</p>
    </button>
  );
}

const tooltipStyle = {
  backgroundColor: '#0d1f35',
  border: '1px solid #1e3a5f',
  borderRadius: '12px',
  color: '#f1f5f9',
  fontSize: '13px',
};

export function AdminDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [topics, setTopics] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [packStatus, setPackStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
    loadPackStatus();
  }, []);

  const loadData = async () => {
    try {
      const [metricsRes, topicsRes] = await Promise.all([
        adminAPI.getMetrics(),
        adminAPI.getTopics(),
      ]);
      setMetrics(metricsRes.data);
      setTopics(topicsRes.data.topics);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
    // Analytics is additive — don't block the dashboard if it fails
    try {
      const res = await adminAPI.getAnalytics(14);
      setAnalytics(res.data);
    } catch (e) { /* older backend */ }
  };

  const loadPackStatus = async () => {
    try {
      const res = await adminAPI.getPackStatus();
      setPackStatus(res.data);
    } catch (error) {
      console.error('Failed to load pack status');
    }
  };

  const handleGeneratePack = async () => {
    try {
      const res = await adminAPI.regeneratePack();
      if (res.data.success) {
        toast.success(`Pack regenerated! Image Quiz in ${res.data.image_quiz_included_in}/11 quizzes`);
      } else {
        toast.success('Today\'s pack generated successfully!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate pack');
    }
  };

  if (loading) {
    return <LoadingSpinner className="py-20" />;
  }

  const totals = analytics?.totals;

  return (
    <div className="max-w-7xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white font-['Space_Grotesk']">Dashboard</h1>
        <p className="text-sm text-slate-400 mt-1">Live overview of SocraQuest.</p>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Users" value={totals?.users ?? metrics?.total_users ?? 0}
          onClick={() => navigate('/admin/users')} />
        <StatCard icon={BookOpen} label="Quizzes Today" value={metrics?.quizzes_played_today ?? 0} />
        <StatCard icon={HelpCircle} label="Active Questions" value={totals?.questions ?? '—'} accent="text-cyan-400"
          onClick={() => navigate('/admin/topics')} />
        <StatCard icon={Flag} label="Open Reports" value={totals?.open_reports ?? 0}
          accent={totals?.open_reports > 0 ? 'text-rose-400' : 'text-emerald-400'}
          onClick={() => navigate('/admin/reports')} />
      </div>

      {/* Analytics charts */}
      {analytics?.series?.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-[#0d1f35] rounded-2xl p-5 border border-slate-800">
            <h2 className="text-base font-semibold text-white mb-1 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-teal-400" /> Daily activity (14 days)
            </h2>
            <p className="text-xs text-slate-500 mb-3">Active users & quiz attempts per day</p>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={analytics.series} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradDau" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2dd4bf" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#2dd4bf" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradAttempts" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#38bdf8" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#38bdf8" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" vertical={false} />
                <XAxis dataKey="day" tick={{ fill: '#64748b', fontSize: 11 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fill: '#64748b', fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Area type="monotone" dataKey="dau" name="Active users" stroke="#2dd4bf" strokeWidth={2} fill="url(#gradDau)" />
                <Area type="monotone" dataKey="attempts" name="Attempts" stroke="#38bdf8" strokeWidth={2} fill="url(#gradAttempts)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-[#0d1f35] rounded-2xl p-5 border border-slate-800">
            <h2 className="text-base font-semibold text-white mb-1 flex items-center gap-2">
              <Users className="w-4 h-4 text-amber-400" /> New signups (14 days)
            </h2>
            <p className="text-xs text-slate-500 mb-3">Registrations per day</p>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={analytics.series} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradNew" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#fbbf24" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#fbbf24" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" vertical={false} />
                <XAxis dataKey="day" tick={{ fill: '#64748b', fontSize: 11 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fill: '#64748b', fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Area type="monotone" dataKey="new_users" name="New users" stroke="#fbbf24" strokeWidth={2} fill="url(#gradNew)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Pack Status */}
      {packStatus && (
        <div className="bg-[#0d1f35] rounded-2xl p-6 border border-slate-800">
          <h2 className="text-xl font-bold text-white mb-4 font-['Space_Grotesk']">Today's Pack Status</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-slate-800/40 rounded-xl">
              <p className="text-sm text-slate-400">Pack Status</p>
              <p className="text-lg font-bold text-white">
                {packStatus.pack_exists ? '✅ Generated' : '⚠️ Not Generated'}
              </p>
            </div>
            <div className="p-4 bg-slate-800/40 rounded-xl">
              <p className="text-sm text-slate-400">Image Quiz Topic</p>
              <p className="text-lg font-bold text-white">
                {packStatus.image_quiz_topic?.exists
                  ? (packStatus.image_quiz_topic.active ? '✅ Active' : '⚠️ Inactive')
                  : '❌ Not Found'}
              </p>
              {packStatus.image_quiz_topic?.exists && (
                <p className="text-xs text-slate-500">
                  {packStatus.image_quiz_topic.question_count} questions
                  {packStatus.image_quiz_topic.has_enough_questions ? ' ✓' : ' (need 33+)'}
                </p>
              )}
            </div>
            <div className="p-4 bg-slate-800/40 rounded-xl">
              <p className="text-sm text-slate-400">Image Quiz in Quizzes</p>
              <p className="text-lg font-bold text-white">
                {packStatus.image_quiz_in_quizzes?.length || 0}/11 quizzes
              </p>
              {packStatus.image_quiz_in_quizzes?.length > 0 && (
                <p className="text-xs text-slate-500">
                  Quiz: {packStatus.image_quiz_in_quizzes.map(i => i + 1).join(', ')}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-[#0d1f35] rounded-2xl p-6 border border-slate-800">
        <h2 className="text-xl font-bold text-white mb-4 font-['Space_Grotesk']">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Button
            onClick={async () => {
              await handleGeneratePack();
              loadPackStatus();
            }}
            className="h-20 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white"
            data-testid="regenerate-pack-button"
          >
            <Calendar className="w-5 h-5 mr-2" />
            🔄 Regenerate Pack
          </Button>
          <Button onClick={() => navigate('/admin/topics')} variant="outline"
            className="h-20 border-slate-700 text-slate-300 hover:bg-slate-800">
            <BookOpen className="w-5 h-5 mr-2" /> Topics & Questions
          </Button>
          <Button onClick={() => navigate('/admin/image-quiz')} variant="outline"
            className="h-20 border-teal-500/40 text-teal-300 hover:bg-teal-500/10" data-testid="image-quiz-button">
            <Image className="w-5 h-5 mr-2" /> Image Quiz
          </Button>
          <Button onClick={() => navigate('/admin/ads')} variant="outline"
            className="h-20 border-slate-700 text-slate-300 hover:bg-slate-800">
            <BarChart className="w-5 h-5 mr-2" /> Ads Placement
          </Button>
          <Button onClick={() => navigate('/admin/notifications')} variant="outline"
            className="h-20 border-slate-700 text-slate-300 hover:bg-slate-800">
            <Bell className="w-5 h-5 mr-2" /> Push Notifications
          </Button>
        </div>
      </div>

      {/* Topics Overview */}
      <div className="bg-[#0d1f35] rounded-2xl p-6 border border-slate-800">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white font-['Space_Grotesk']">Topics Overview</h2>
          <Button onClick={() => navigate('/admin/topics')} size="sm" className="bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold">View All</Button>
        </div>
        <p className="text-slate-400">Total Topics: <span className="font-semibold text-white">{topics.length}</span></p>
        <p className="text-slate-400">Active Topics: <span className="font-semibold text-white">{topics.filter(t => t.active).length}</span></p>
      </div>
    </div>
  );
}
